import React, { useState, useRef, useEffect } from 'react';
import { AutoComplete, Input, Spin, Typography } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { FormLabel } from '@/components/Form';
import ErrorText from '../ErrorText';
import { lookupForPick } from './lookupForPick';

interface GoogleCustomLocationFieldProps {
  name: string;
  control?: any;
  placeholder?: string;
  error?: string;
  label?: string;
  subLabel?: string;
  size?: 'large' | 'middle';
  required?: boolean;
  disabled?: boolean;
  location?: 'australia' | 'overseas';
  setPropertyDetailsObject?: (details: any) => void;
  /**
   * Fires with the chosen prediction when the user picks one out of the
   * dropdown, and with `null` as soon as they type again. Lets a form require
   * that the stored address actually came from Google rather than free text.
   */
  onSelectedAddressChange?: (address: string | null) => void;
}

type AddressPart = { label: string; value: string; required?: boolean };

// Labels each piece of the Google result so the user can see what was
// recognised (and what's missing) — address formats differ by country.
const getAddressParts = (c: Record<string, string>): AddressPart[] => {
  const isAU = c.countryCode === 'AU';
  return [
    { label: 'Unit', value: c.apartment },
    {
      label: 'Street',
      value: [c.streetNumber, c.street].filter(Boolean).join(' '),
      required: true,
    },
    isAU
      ? { label: 'Suburb', value: c.suburb, required: true }
      : { label: 'Area', value: c.sublocality },
    ...(isAU ? [] : [{ label: 'City', value: c.city, required: true }]),
    {
      label: isAU ? 'State' : 'State / Region',
      value: c.state,
      required: true,
    },
    { label: 'Postcode', value: c.postcode, required: true },
    { label: 'Country', value: c.country, required: true },
  ];
};

const GoogleCustomLocationField: React.FC<GoogleCustomLocationFieldProps> = ({
  name,
  control,
  placeholder,
  error,
  label,
  subLabel,
  size = 'middle',
  required = false,
  disabled = false,
  location = 'australia',
  setPropertyDetailsObject,
  onSelectedAddressChange,
}) => {
  const [options, setOptions] = useState<{ value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [addressParts, setAddressParts] = useState<AddressPart[] | null>(null);
  const [debouncedInput] = useDebounce(inputValue, 400);

  // Places API (New): AutocompleteService / PlacesService are unavailable to
  // new Google Cloud projects. Predictions are kept by their display text so
  // the selected one can be resolved to a Place without a second lookup.
  const predictionsRef = useRef<
    Map<string, google.maps.places.PlacePrediction>
  >(new Map());
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const lastSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!window.google?.maps?.places) return;

    if (debouncedInput.length < 4) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    sessionTokenRef.current ??=
      new google.maps.places.AutocompleteSessionToken();
    setIsLoading(true);
    google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: debouncedInput,
      includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
      ...(location === 'australia' && { includedRegionCodes: ['au'] }),
      sessionToken: sessionTokenRef.current,
    })
      .then(({ suggestions }) => {
        if (cancelled) return;
        predictionsRef.current = new Map();
        suggestions.forEach(({ placePrediction: p }) => {
          if (p) predictionsRef.current.set(p.text.text, p);
        });
        setOptions(
          [...predictionsRef.current.keys()].map((value) => ({ value })),
        );
      })
      .catch((err) => {
        // REQUEST_DENIED etc. (billing, Places API (New) not enabled, key
        // restrictions) would otherwise fail silently.
        console.error('Google Places autocomplete failed:', err);
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      // A newer search turns it back on; a short input never does.
      setIsLoading(false);
    };
  }, [debouncedInput]);

  const getPropertyDetails = async (
    prediction: google.maps.places.PlacePrediction,
    picked: string,
  ) => {
    const place = prediction.toPlace();
    try {
      const fresh = await lookupForPick(
        picked,
        () => lastSelectedRef.current,
        () =>
          place.fetchFields({
            fields: ['addressComponents', 'formattedAddress'],
          }),
      );
      if (!fresh) return;
    } catch (err) {
      console.error('Google Places details failed:', err);
      return;
    } finally {
      // fetchFields ends the billing session; the next search starts a new one.
      sessionTokenRef.current = null;
    }

    const components = place.addressComponents || [];

    const getComponent = (type: string) =>
      components.find((c) => c.types.includes(type))?.longText || '';

    const apartment = getComponent('subpremise');
    const streetNumber = getComponent('street_number');
    const street = getComponent('route');
    const suburb = getComponent('locality');
    const state = getComponent('administrative_area_level_1');
    const postcode = getComponent('postal_code');
    // Outside Australia `locality` is the city and the suburb is a
    // sublocality / neighborhood; UK towns come as `postal_town`.
    const city = suburb || getComponent('postal_town');
    const sublocality =
      getComponent('sublocality') || getComponent('neighborhood');
    const country = components.find((c) => c.types.includes('country'));

    const details = {
      apartment,
      streetNumber,
      street,
      suburb,
      state,
      postcode,
      city,
      sublocality,
      country: country?.longText || '',
      countryCode: country?.shortText || '',
      fullAddress: place.formattedAddress || '',
    };
    setAddressParts(getAddressParts(details));
    setPropertyDetailsObject?.(details);
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const handleChange = (value: string) => {
            setInputValue(value);
            field.onChange(value);
            // Editing away from the chosen prediction means the value is no
            // longer a confirmed Google result. Comparing against the last
            // selection keeps this safe if antd also fires onSearch on select.
            if (value !== lastSelectedRef.current) {
              lastSelectedRef.current = null;
              setAddressParts(null);
              onSelectedAddressChange?.(null);
            }
          };

          const handleSelect = (value: string) => {
            setInputValue(value);
            field.onChange(value);
            lastSelectedRef.current = value;
            onSelectedAddressChange?.(value);

            const prediction = predictionsRef.current.get(value);
            if (prediction) getPropertyDetails(prediction, value);
          };

          return (
            <>
              {label && (
                <FormLabel
                  required={required}
                  label={label}
                  subLabel={subLabel}
                />
              )}
              <AutoComplete
                style={{ width: '100%' }}
                options={options}
                onSearch={handleChange}
                onSelect={handleSelect}
                value={field.value}
                size={size}
                disabled={disabled}
                status={error ? 'error' : undefined}
              >
                <Input
                  size={size}
                  placeholder={placeholder || 'Search address...'}
                  prefix={<EnvironmentOutlined />}
                  suffix={isLoading ? <Spin size="small" /> : null}
                  status={error ? 'error' : undefined}
                />
              </AutoComplete>
              {error && <ErrorText error={error} />}
              {addressParts && field.value === lastSelectedRef.current && (
                <AddressBreakdown parts={addressParts} />
              )}
            </>
          );
        }}
      />
    </>
  );
};

const AddressBreakdown: React.FC<{ parts: AddressPart[] }> = ({ parts }) => {
  const found = parts.filter((p) => p.value);
  const missing = parts.filter((p) => !p.value && p.required);
  return (
    <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.6 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {found.map((p, i) => (
          <span key={p.label}>
            {i > 0 && ' · '}
            {p.label}: <strong>{p.value}</strong>
          </span>
        ))}
      </Typography.Text>
      {missing.length > 0 && (
        <div>
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            Not found: {missing.map((p) => p.label).join(', ')}. Please check
            the address is complete.
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default GoogleCustomLocationField;
